import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Trash2, Plus, KeyRound, ShieldCheck } from "lucide-react";

const LS_KEY = "omni-passwords";
function load() { try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; } }
function save(list) { localStorage.setItem(LS_KEY, JSON.stringify(list)); }

export default function PasswordsPage() {
  const [items, setItems] = useState([]);
  const [showId, setShowId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [site, setSite] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => { setItems(load()); }, []);

  const add = (e) => {
    e.preventDefault();
    if (!site || !username || !password) return;
    const item = { id: Date.now().toString(), site, username, password, created: new Date().toISOString() };
    const next = [item, ...items];
    setItems(next); save(next);
    setSite(""); setUsername(""); setPassword(""); setAdding(false);
  };

  const remove = (id) => {
    const next = items.filter((x) => x.id !== id);
    setItems(next); save(next);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-12" data-testid="passwords-page">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
            <KeyRound size={28} /> Mots de passe
          </h1>
          <Button onClick={() => setAdding((a) => !a)} className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="add-password-btn">
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-6 flex items-center gap-2">
          <ShieldCheck size={14} className="text-emerald-600" /> Stockés localement dans votre navigateur. Aucun envoi serveur.
        </p>

        {adding && (
          <form onSubmit={add} className="border border-[var(--omni-border)] rounded-lg p-5 mb-6 space-y-3" data-testid="add-password-form">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Site</Label>
                <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="github.com" data-testid="pwd-site-input" />
              </div>
              <div>
                <Label className="text-xs">Nom d'utilisateur</Label>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="vous@email.com" data-testid="pwd-user-input" />
              </div>
              <div>
                <Label className="text-xs">Mot de passe</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="pwd-pass-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white rounded-full" data-testid="pwd-save-btn">Enregistrer</Button>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)} data-testid="pwd-cancel-btn">Annuler</Button>
            </div>
          </form>
        )}

        {items.length === 0 ? (
          <div className="border border-dashed border-[var(--omni-border)] rounded-lg p-12 text-center text-gray-500">
            Aucun mot de passe enregistré.
          </div>
        ) : (
          <div className="border border-[var(--omni-border)] rounded-lg overflow-hidden divide-y divide-[var(--omni-border)]">
            {items.map((it) => (
              <div key={it.id} className="px-4 py-3 flex items-center gap-4 hover:bg-[var(--omni-surface)]" data-testid={`pwd-item-${it.id}`}>
                <img src={`https://www.google.com/s2/favicons?domain=${it.site}&sz=32`} alt="" className="w-6 h-6" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.site}</div>
                  <div className="text-xs text-gray-500 truncate">{it.username}</div>
                </div>
                <code className="text-xs font-mono text-gray-700 select-all">
                  {showId === it.id ? it.password : "••••••••"}
                </code>
                <button onClick={() => setShowId(showId === it.id ? null : it.id)} className="p-2 rounded-md hover:bg-gray-100" data-testid={`pwd-toggle-${it.id}`}>
                  {showId === it.id ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-md hover:bg-gray-100 text-red-600" data-testid={`pwd-delete-${it.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
